import type { Lang } from "@/utils/i18n";

/**
 * Product and navigation copy for the public home.
 * Missing keys fall back to English.
 */
type Dict = Record<string, string>;

const en: Dict = {
  metaTitle: "Moova TV — The player for your Smart TV, with your phone in charge",
  metaDesc:
    "Media player for Samsung, LG, Roku, Fire TV, Android TV and Apple TV. Your playlists, a 7-day guide, parental PIN and your phone as the remote. 7 days free.",

  heroEyebrow: "Media player for Smart TVs",
  heroH1a: "Your TV. Your sources.",
  heroH1b: "Your phone in charge.",
  heroLead:
    "Moova turns the playlists you already have into a beautiful catalog on the TV. Pair your phone and pick there what you watch here.",
  heroCta: "Try free for 7 days",
  heroCta2: "See plans",
  heroTrust1: "No card needed",
  heroTrust2: "Activate with a QR code",
  heroTrust3: "We never sell content",
  heroRunsOn: "Runs on",

  stBrowse: "Pick on your phone",
  stSelect: "Title selected",
  stSend: "Sending to the TV",
  stPlaying: "Playing on the TV",
  stRemote: "Your phone is the remote now",
  stWatch: "Watch on TV",
  stHome: "Home",
  stMovies: "Movies",
  stSeries: "Series",
  stLive: "Live",
  stList: "My List",
  stContinue: "Continue watching",
  tvRowCw: "Movies in Progress",
  tvRowLast: "Last Added Movies",
  stSettings: "Settings",

  devKicker: "Devices",
  devTitleA: "Your TV",
  devTitleB: "is probably already on the list.",
  devLead:
    "One player, the same layout and the same features on every platform. Pick yours to see where it installs from.",
  devAvailable: "Available",
  devBeta: "Beta",
  devSoon: "Coming soon",
  devStore: "Installs from",
  devModels: "Devices",
  devRemote: "Works with the original remote and with your phone",
  devPhoneTitle: "And in your pocket",
  devPhoneLead: "The Moova phone app pairs with the compatible TVs.",
  devPhoneAndroid: "Android · Google Play",
  devPhoneIos: "iPhone · App Store",

  dSamsungOs: "Tizen",
  dSamsungNote: "Samsung Smart TVs",
  dSamsungStore: "Samsung TV Apps",
  dLgOs: "webOS",
  dLgNote: "LG Smart TVs",
  dLgStore: "LG Content Store",
  dRokuOs: "Roku OS",
  dRokuNote: "Roku TV models, Roku Express, Roku Streaming Stick and Roku Ultra",
  dRokuStore: "Roku Channel Store",
  dFireOs: "Fire OS and Vega OS",
  dFireNote: "Fire TV Stick, Fire TV Cube and Fire TV Edition TVs",
  dFireStore: "Amazon Appstore",
  dAndroidOs: "Android TV and Google TV",
  dAndroidNote: "TCL, Sony, Philips, Xiaomi, Chromecast with Google TV and TV boxes",
  dAndroidStore: "Google Play",
  dAppleOs: "tvOS",
  dAppleNote: "Apple TV HD and Apple TV 4K",
  dAppleStore: "App Store",

  cmKicker: "Phone as the remote",
  cmTitleA: "Pick on the phone.",
  cmTitleB: "Watch on the TV.",
  cm1t: "Pair in seconds",
  cm1d: "Open Connect to TV on Moova, scan the QR with the app or type the code shown on the TV. The TV confirms and you are in.",
  cm2t: "Browse without typing on the TV",
  cm2d:
    "Movies, series and channels on your phone. Tap a title and it plays on the TV, with pause, volume and channel zapping in your hand.",
  cm3t: "Search like a person",
  cm3d: "Type on your phone keyboard and see results right away. No more letter by letter with the remote.",
  cm4t: "Everything in sync",
  cm4d:
    "Favourites, continue watching and playlists stay the same on the TV and on the phone. Switch playlist in the app and the TV follows.",
  cmConnect: "Connect to TV",
  cmConnectSub: "Scan the QR shown on your TV",
  cmCode: "Or type the code",
  cmConnected: "TV connected",
  cmSearch: "Search movies…",
  cmSyncTitle: "Synced with the TV",
  cmSyncFav: "Favourites",
  cmSyncCw: "Continue watching",
  cmSyncPl: "Playlists",
  cmNowPlaying: "Playing on the TV",

  ftKicker: "Features",
  ftTitleA: "Built for watching,",
  ftTitleB: "not for configuring.",
  f1t: "Pick up where you left off",
  f1d:
    "Movies and series in progress right on the home screen. The series remembers the episode, the movie remembers the minute, and the right button shows up: Continue or Next episode.",
  f2t: "Live TV with EPG",
  f2d: "Today and the next 7 days in Live mode, with catch-up whenever your server offers it.",
  f3t: "A parental PIN that holds",
  f3d:
    "Lock categories with a PIN. They disappear from search and highlights. Privacy mode pauses the watch history whenever you want.",
  f4t: "Several playlists",
  f4d: "Add, rename, switch and protect each playlist with a PIN. From the TV or from the phone.",
  f5t: "Fast search",
  f5d: "On-screen keyboard on the TV or typing from the phone, with results filtered by the parental PIN.",
  f6t: "Opens fast",
  f6d:
    "The catalog is cached on the device. Moova opens straight on the home screen and only downloads everything again when you ask.",
  f7t: "7 languages",
  f7d: "Portuguese, English, Spanish, French, German, Italian and Ukrainian, with adjustable time format and time zone.",
  f8t: "Playback that holds up",
  f8d: "Channels in TS or HLS with automatic fallback, and channel zapping without stopping the player.",
  ilMovies: "Movies in progress",
  ilSeries: "Series in progress",
  ilRecent: "Recent channels",
  ilLeft: "min left",
  ilNext: "Next episode",
  ilToday: "Today",
  ilNow: "Now",
  ilLocked: "Locked",
  ilPin: "Enter PIN",
  ilPl1: "Living room",
  ilPl2: "Bedroom",
  ilPl3: "Cabin",
  ilCached: "Catalog cached",
  ilReady: "Ready",
  ilFallback: "automatic fallback",
  ilPrivacy: "Privacy mode",

  stepKicker: "Getting started",
  stepTitle: "Three steps and the TV is ready",
  step1t: "Install and open Moova on the TV",
  step1d: "The first screen shows a QR code and your TV's device address. The 7-day trial starts right there, no card.",
  step2t: "Add your playlist",
  step2d: "Paste the URL of your playlist or media server on the site or in the app. The TV loads the catalog and you are watching.",
  step3t: "Watch, then pick a plan if you like it",
  step3d: "Scan the QR or go to moova.tv/activation whenever you want to continue after the trial.",
  stepNote: "After the trial, pick the annual or the lifetime plan. No plan includes content.",

  pnpKicker: "Player, not provider",
  pnpTitleA: "We sell the player.",
  pnpTitleB: "Never the content.",
  pnpLead:
    "Moova ships with no channels, no playlists and no content subscriptions. You bring your own sources; we make them look and feel premium on your TV.",
  pnpYesT: "What you get",
  pnpYes1: "A premium player for your own playlists and media sources",
  pnpYes2: "Program guide, favourites, parental PIN and phone sync",
  pnpYes3: "Phone as the remote on every compatible TV",
  pnpNoT: "What we never sell",
  pnpNo1: "No TV channels or live streams",
  pnpNo2: "No playlists, bundles or content subscriptions",
  pnpNo3: "No content hosting: your sources stay yours",

  prKicker: "Plans",
  prTitleA: "One simple price.",
  prTitleB: "No surprises.",
  prSub: "Try it free for 7 days. Then choose how to continue.",
  prAnnual: "Annual",
  prAnnualDesc: "12 months of access",
  prAnnualUnit: "/year",
  prA1: "Full player on every compatible TV",
  prA2: "Phone as the remote and sync",
  prA3: "Program guide, parental PIN and several playlists",
  prA4: "Updates during the period",
  prAnnualCta: "Start annual plan",
  prLife: "Lifetime",
  prLifeDesc: "One-time payment",
  prLifeUnit: "forever",
  prL1: "Everything in Annual",
  prL2: "Lifetime access, no renewal",
  prL3: "Future updates included",
  prL4: "Email support included",
  prLifeCta: "Get lifetime access",
  prBest: "Best value",
  prBen1: "7-day free trial",
  prBen2: "Secure payment",
  prBen3: "One licence per device",
  prDisclaimer: "Plans unlock the player. No plan includes channels, playlists or content.",

  rsKicker: "Resellers",
  rsTitle: "Do you have customers with Smart TVs?",
  rsLead: "Create and manage activations with credits in your own panel.",
  rsCta: "Become a reseller",

  fiTitle: "Your TV is one QR code away.",
  fiLead: "Install Moova, add your playlist and watch with your phone in charge. 7 days free, no card.",
  fiCta: "Activate my TV",
  fiTrademarks: "Roku, Roku TV and Roku Streaming Stick are trademarks of Roku, Inc. Android, Android TV, Google TV, Chromecast and Google Play are trademarks of Google LLC. Apple TV, tvOS, iPhone and App Store are trademarks of Apple Inc. Amazon, Fire TV and Amazon Appstore are trademarks of Amazon.com, Inc. Samsung, Tizen, LG and webOS belong to their respective owners. Moova is not affiliated with or endorsed by any of them.",
  fiMicro: "No card on the free trial · We never sell content",
};

const pt: Dict = {
  metaTitle: "Moova TV — O player da sua Smart TV, com o celular no comando",
  metaDesc:
    "Player de mídia para Samsung, LG, Roku, Fire TV, Android TV e Apple TV. Suas playlists, guia de 7 dias, PIN parental e o celular como controle. 7 dias grátis.",

  heroEyebrow: "Player de mídia para Smart TV",
  heroH1a: "Sua TV. Suas fontes.",
  heroH1b: "Seu celular no comando.",
  heroLead:
    "A Moova transforma as playlists que você já tem em um catálogo bonito na TV. Pareie o celular e escolha ali o que assistir aqui.",
  heroCta: "Testar grátis por 7 dias",
  heroCta2: "Ver planos",
  heroTrust1: "Sem cartão",
  heroTrust2: "Ativação por QR code",
  heroTrust3: "Não vendemos conteúdo",
  heroRunsOn: "Roda em",

  stBrowse: "Escolha no celular",
  stSelect: "Título selecionado",
  stSend: "Enviando para a TV",
  stPlaying: "Reproduzindo na TV",
  stRemote: "O celular virou o controle",
  stWatch: "Assistir na TV",
  stHome: "Início",
  stMovies: "Filmes",
  stSeries: "Séries",
  stLive: "Ao vivo",
  stList: "Minha lista",
  stContinue: "Continuar assistindo",
  tvRowCw: "Filmes em Andamento",
  tvRowLast: "Filmes Adicionados Recentemente",
  stSettings: "Configurações",

  devKicker: "Dispositivos",
  devTitleA: "Sua TV",
  devTitleB: "provavelmente já está na lista.",
  devLead:
    "Um único player, o mesmo layout e as mesmas funções em cada plataforma. Escolha a sua para ver de onde instalar.",
  devAvailable: "Disponível",
  devBeta: "Beta",
  devSoon: "Em breve",
  devStore: "Instala pela",
  devModels: "Aparelhos",
  devRemote: "Funciona com o controle original e com o celular",
  devPhoneTitle: "E no bolso",
  devPhoneLead: "O app Moova para celular pareia com as TVs compatíveis.",
  devPhoneAndroid: "Android · Google Play",
  devPhoneIos: "iPhone · App Store",

  dSamsungOs: "Tizen",
  dSamsungNote: "Smart TVs Samsung",
  dSamsungStore: "Samsung TV Apps",
  dLgOs: "webOS",
  dLgNote: "Smart TVs LG",
  dLgStore: "LG Content Store",
  dRokuOs: "Roku OS",
  dRokuNote: "Modelos Roku TV, Roku Express, Roku Streaming Stick e Roku Ultra",
  dRokuStore: "Roku Channel Store",
  dFireOs: "Fire OS e Vega OS",
  dFireNote: "Fire TV Stick, Fire TV Cube e TVs Fire TV Edition",
  dFireStore: "Amazon Appstore",
  dAndroidOs: "Android TV e Google TV",
  dAndroidNote: "TCL, Sony, Philips, Xiaomi, Chromecast com Google TV e TV boxes",
  dAndroidStore: "Google Play",
  dAppleOs: "tvOS",
  dAppleNote: "Apple TV HD e Apple TV 4K",
  dAppleStore: "App Store",

  cmKicker: "Celular como controle",
  cmTitleA: "Escolha no celular.",
  cmTitleB: "Assista na TV.",
  cm1t: "Pareie em segundos",
  cm1d: "Abra Conectar à TV na Moova, escaneie o QR com o app ou digite o código que aparece na TV. A TV confirma e pronto.",
  cm2t: "Navegue sem digitar na TV",
  cm2d:
    "Filmes, séries e canais no seu celular. Toque em um título e ele toca na TV, com pausa, volume e troca de canal na palma da mão.",
  cm3t: "Busque como gente",
  cm3d: "Digite no teclado do celular e veja os resultados na hora. Nada de letra por letra no controle.",
  cm4t: "Tudo sincronizado",
  cm4d:
    "Favoritos, continuar assistindo e playlists ficam iguais na TV e no celular. Trocou de playlist no app, a TV troca junto.",
  cmConnect: "Conectar à TV",
  cmConnectSub: "Escaneie o QR que aparece na TV",
  cmCode: "Ou digite o código",
  cmConnected: "TV conectada",
  cmSearch: "Buscar filmes…",
  cmSyncTitle: "Sincronizado com a TV",
  cmSyncFav: "Favoritos",
  cmSyncCw: "Continuar assistindo",
  cmSyncPl: "Playlists",
  cmNowPlaying: "Tocando na TV",

  ftKicker: "Recursos",
  ftTitleA: "Feito para assistir,",
  ftTitleB: "não para configurar.",
  f1t: "Continue de onde parou",
  f1d:
    "Filmes e séries em andamento na tela inicial. A série lembra o episódio, o filme lembra o minuto, e o botão certo aparece: Continuar ou Próximo episódio.",
  f2t: "TV ao vivo com EPG",
  f2d: "Hoje e os próximos 7 dias no modo ao vivo, com catch-up sempre que o seu servidor oferecer.",
  f3t: "PIN parental de verdade",
  f3d:
    "Bloqueie categorias com PIN. Elas somem da busca e dos destaques. O Modo privacidade pausa o histórico quando você quiser.",
  f4t: "Várias playlists",
  f4d: "Adicione, renomeie, troque e proteja cada playlist com PIN. Pela TV ou pelo celular.",
  f5t: "Busca rápida",
  f5d: "Teclado na TV ou digitação pelo celular, com resultados filtrados pelo PIN parental.",
  f6t: "Abre rápido",
  f6d:
    "O catálogo fica em cache no aparelho. A Moova abre direto na tela inicial e só baixa tudo de novo quando você pedir.",
  f7t: "7 idiomas",
  f7d: "Português, inglês, espanhol, francês, alemão, italiano e ucraniano, com formato de hora e fuso ajustáveis.",
  f8t: "Reprodução que não trava",
  f8d: "Canais em TS ou HLS com fallback automático e troca de canal sem parar o player.",
  ilMovies: "Filmes em andamento",
  ilSeries: "Séries em andamento",
  ilRecent: "Canais recentes",
  ilLeft: "min restantes",
  ilNext: "Próximo episódio",
  ilToday: "Hoje",
  ilNow: "Agora",
  ilLocked: "Bloqueada",
  ilPin: "Digite o PIN",
  ilPl1: "Sala",
  ilPl2: "Quarto",
  ilPl3: "Sítio",
  ilCached: "Catálogo em cache",
  ilReady: "Pronto",
  ilFallback: "fallback automático",
  ilPrivacy: "Modo privacidade",

  stepKicker: "Como começar",
  stepTitle: "Três passos e a TV está pronta",
  step1t: "Instale e abra a Moova na TV",
  step1d: "A primeira tela mostra um QR code e o endereço do seu aparelho. O teste de 7 dias começa ali mesmo, sem cartão.",
  step2t: "Adicione sua playlist",
  step2d: "Cole a URL da sua playlist ou servidor de mídia pelo site ou pelo app. A TV carrega o catálogo e você já assiste.",
  step3t: "Assista e, se gostar, escolha um plano",
  step3d: "Escaneie o QR ou acesse moova.tv/activation quando quiser continuar depois do teste.",
  stepNote: "Depois do teste, escolha o plano anual ou o vitalício. Nenhum plano inclui conteúdo.",

  pnpKicker: "Player, não provedor",
  pnpTitleA: "Vendemos o player.",
  pnpTitleB: "Nunca o conteúdo.",
  pnpLead:
    "A Moova não vem com canais, playlists nem assinaturas de conteúdo. Você traz suas próprias fontes; nós fazemos elas parecerem premium na sua TV.",
  pnpYesT: "O que você recebe",
  pnpYes1: "Um player premium para suas próprias playlists e fontes de mídia",
  pnpYes2: "Guia de programação, favoritos, PIN parental e sincronização com o celular",
  pnpYes3: "Celular como controle em todas as TVs compatíveis",
  pnpNoT: "O que nunca vendemos",
  pnpNo1: "Nenhum canal de TV ou transmissão ao vivo",
  pnpNo2: "Nenhuma playlist, pacote ou assinatura de conteúdo",
  pnpNo3: "Nenhuma hospedagem de conteúdo: suas fontes continuam suas",

  prKicker: "Planos",
  prTitleA: "Um preço simples.",
  prTitleB: "Sem surpresas.",
  prSub: "Teste 7 dias grátis. Depois, escolha como continuar.",
  prAnnual: "Anual",
  prAnnualDesc: "12 meses de acesso",
  prAnnualUnit: "/ano",
  prA1: "Player completo em todas as TVs compatíveis",
  prA2: "Celular como controle e sincronização",
  prA3: "Guia de programação, PIN parental e várias playlists",
  prA4: "Atualizações durante o período",
  prAnnualCta: "Assinar plano anual",
  prLife: "Vitalício",
  prLifeDesc: "Pagamento único",
  prLifeUnit: "para sempre",
  prL1: "Tudo do plano Anual",
  prL2: "Acesso vitalício, sem renovação",
  prL3: "Atualizações futuras incluídas",
  prL4: "Suporte por e-mail incluído",
  prLifeCta: "Obter acesso vitalício",
  prBest: "Melhor custo",
  prBen1: "7 dias grátis",
  prBen2: "Pagamento seguro",
  prBen3: "Uma licença por aparelho",
  prDisclaimer: "Os planos desbloqueiam o player. Nenhum plano inclui canais, playlists ou conteúdo.",

  rsKicker: "Revenda",
  rsTitle: "Tem clientes com Smart TV?",
  rsLead: "Crie e gerencie ativações com créditos em um painel próprio.",
  rsCta: "Seja um revendedor",

  fiTitle: "Sua TV está a um QR code de distância.",
  fiLead: "Instale a Moova, adicione sua playlist e assista com o celular no comando. 7 dias grátis, sem cartão.",
  fiCta: "Ativar minha TV",
  fiTrademarks: "Roku, Roku TV e Roku Streaming Stick são marcas da Roku, Inc. Android, Android TV, Google TV, Chromecast e Google Play são marcas da Google LLC. Apple TV, tvOS, iPhone e App Store são marcas da Apple Inc. Amazon, Fire TV e Amazon Appstore são marcas da Amazon.com, Inc. Samsung, Tizen, LG e webOS pertencem aos seus respectivos donos. A Moova não é afiliada nem endossada por nenhuma delas.",
  fiMicro: "Sem cartão no teste grátis · Não vendemos conteúdo",
};

const es: Dict = {
  metaTitle: "Moova TV — El reproductor de tu Smart TV, con el móvil al mando",
  metaDesc:
    "Reproductor multimedia para Samsung, LG, Roku, Fire TV, Android TV y Apple TV. Tus listas, guía de 7 días, PIN parental y el móvil como mando. 7 días gratis.",

  heroEyebrow: "Reproductor multimedia para Smart TV",
  heroH1a: "Tu TV. Tus fuentes.",
  heroH1b: "Tu móvil al mando.",
  heroLead:
    "Moova convierte las listas que ya tienes en un catálogo bonito en la TV. Empareja el móvil y elige allí lo que ves aquí.",
  heroCta: "Probar gratis 7 días",
  heroCta2: "Ver planes",
  heroTrust1: "Sin tarjeta",
  heroTrust2: "Activación por código QR",
  heroTrust3: "No vendemos contenido",
  heroRunsOn: "Funciona en",

  stBrowse: "Elige en el móvil",
  stSelect: "Título seleccionado",
  stSend: "Enviando a la TV",
  stPlaying: "Reproduciendo en la TV",
  stRemote: "El móvil ahora es el mando",
  stWatch: "Ver en la TV",
  stHome: "Inicio",
  stMovies: "Películas",
  stSeries: "Series",
  stLive: "En vivo",
  stList: "Mi lista",
  stContinue: "Seguir viendo",
  tvRowCw: "Películas en curso",
  tvRowLast: "Películas Añadidas Recientemente",
  stSettings: "Configuración",

  devKicker: "Dispositivos",
  devTitleA: "Tu TV",
  devTitleB: "probablemente ya está en la lista.",
  devLead:
    "Un solo reproductor, el mismo diseño y las mismas funciones en cada plataforma. Elige la tuya para ver desde dónde se instala.",
  devAvailable: "Disponible",
  devBeta: "Beta",
  devSoon: "Próximamente",
  devStore: "Se instala desde",
  devModels: "Equipos",
  devRemote: "Funciona con el mando original y con el móvil",
  devPhoneTitle: "Y en el bolsillo",
  devPhoneLead: "La app Moova para móvil se empareja con las TVs compatibles.",
  devPhoneAndroid: "Android · Google Play",
  devPhoneIos: "iPhone · App Store",

  dSamsungOs: "Tizen",
  dSamsungNote: "Smart TVs Samsung",
  dSamsungStore: "Samsung TV Apps",
  dLgOs: "webOS",
  dLgNote: "Smart TVs LG",
  dLgStore: "LG Content Store",
  dRokuOs: "Roku OS",
  dRokuNote: "Modelos Roku TV, Roku Express, Roku Streaming Stick y Roku Ultra",
  dRokuStore: "Roku Channel Store",
  dFireOs: "Fire OS y Vega OS",
  dFireNote: "Fire TV Stick, Fire TV Cube y TVs Fire TV Edition",
  dFireStore: "Amazon Appstore",
  dAndroidOs: "Android TV y Google TV",
  dAndroidNote: "TCL, Sony, Philips, Xiaomi, Chromecast con Google TV y TV boxes",
  dAndroidStore: "Google Play",
  dAppleOs: "tvOS",
  dAppleNote: "Apple TV HD y Apple TV 4K",
  dAppleStore: "App Store",

  cmKicker: "El móvil como mando",
  cmTitleA: "Elige en el móvil.",
  cmTitleB: "Mira en la TV.",
  cm1t: "Empareja en segundos",
  cm1d: "Abre Conectar a la TV en Moova, escanea el QR con la app o escribe el código que aparece en la TV. La TV confirma y listo.",
  cm2t: "Navega sin teclear en la TV",
  cm2d:
    "Películas, series y canales en tu móvil. Toca un título y se reproduce en la TV, con pausa, volumen y cambio de canal en la mano.",
  cm3t: "Busca como una persona",
  cm3d: "Escribe en el teclado del móvil y ve los resultados al instante. Se acabó letra por letra con el mando.",
  cm4t: "Todo sincronizado",
  cm4d:
    "Favoritos, seguir viendo y listas se mantienen iguales en la TV y en el móvil. Cambias de lista en la app y la TV cambia contigo.",
  cmConnect: "Conectar a la TV",
  cmConnectSub: "Escanea el QR que aparece en tu TV",
  cmCode: "O escribe el código",
  cmConnected: "TV conectada",
  cmSearch: "Buscar películas…",
  cmSyncTitle: "Sincronizado con la TV",
  cmSyncFav: "Favoritos",
  cmSyncCw: "Seguir viendo",
  cmSyncPl: "Listas",
  cmNowPlaying: "Reproduciendo en la TV",

  ftKicker: "Funciones",
  ftTitleA: "Hecho para ver,",
  ftTitleB: "no para configurar.",
  f1t: "Sigue donde lo dejaste",
  f1d:
    "Películas y series en curso en la pantalla de inicio. La serie recuerda el episodio, la película recuerda el minuto, y aparece el botón correcto: Continuar o Siguiente episodio.",
  f2t: "TV en vivo con EPG",
  f2d: "Hoy y los próximos 7 días en modo en vivo, con catch-up siempre que tu servidor lo ofrezca.",
  f3t: "Un PIN parental de verdad",
  f3d:
    "Bloquea categorías con PIN. Desaparecen de la búsqueda y de los destacados. El modo privacidad pausa el historial cuando quieras.",
  f4t: "Varias listas",
  f4d: "Añade, renombra, cambia y protege cada lista con PIN. Desde la TV o desde el móvil.",
  f5t: "Búsqueda rápida",
  f5d: "Teclado en la TV o escritura desde el móvil, con resultados filtrados por el PIN parental.",
  f6t: "Abre rápido",
  f6d:
    "El catálogo queda en caché en el equipo. Moova abre directo en la pantalla de inicio y solo vuelve a descargar todo cuando tú lo pides.",
  f7t: "7 idiomas",
  f7d: "Portugués, inglés, español, francés, alemán, italiano y ucraniano, con formato de hora y zona horaria ajustables.",
  f8t: "Reproducción que aguanta",
  f8d: "Canales en TS o HLS con fallback automático y cambio de canal sin detener el reproductor.",
  ilMovies: "Películas en curso",
  ilSeries: "Series en curso",
  ilRecent: "Canales recientes",
  ilLeft: "min restantes",
  ilNext: "Siguiente episodio",
  ilToday: "Hoy",
  ilNow: "Ahora",
  ilLocked: "Bloqueada",
  ilPin: "Escribe el PIN",
  ilPl1: "Salón",
  ilPl2: "Dormitorio",
  ilPl3: "Casa rural",
  ilCached: "Catálogo en caché",
  ilReady: "Listo",
  ilFallback: "fallback automático",
  ilPrivacy: "Modo privacidad",

  stepKicker: "Cómo empezar",
  stepTitle: "Tres pasos y la TV está lista",
  step1t: "Instala y abre Moova en la TV",
  step1d: "La primera pantalla muestra un código QR y la dirección de tu equipo. La prueba de 7 días empieza ahí mismo, sin tarjeta.",
  step2t: "Añade tu lista",
  step2d: "Pega la URL de tu lista o servidor multimedia en la web o en la app. La TV carga el catálogo y ya estás viendo.",
  step3t: "Mira y, si te gusta, elige un plan",
  step3d: "Escanea el QR o entra en moova.tv/activation cuando quieras continuar tras la prueba.",
  stepNote: "Tras la prueba, elige el plan anual o el vitalicio. Ningún plan incluye contenido.",

  pnpKicker: "Reproductor, no proveedor",
  pnpTitleA: "Vendemos el reproductor.",
  pnpTitleB: "Nunca el contenido.",
  pnpLead:
    "Moova no incluye canales, listas ni suscripciones de contenido. Tú traes tus propias fuentes; nosotros hacemos que se vean premium en tu TV.",
  pnpYesT: "Lo que recibes",
  pnpYes1: "Un reproductor premium para tus propias listas y fuentes multimedia",
  pnpYes2: "Guía de programación, favoritos, PIN parental y sincronización con el móvil",
  pnpYes3: "El móvil como mando en todas las TVs compatibles",
  pnpNoT: "Lo que nunca vendemos",
  pnpNo1: "Ningún canal de TV ni emisión en vivo",
  pnpNo2: "Ninguna lista, paquete ni suscripción de contenido",
  pnpNo3: "Ningún alojamiento de contenido: tus fuentes siguen siendo tuyas",

  prKicker: "Planes",
  prTitleA: "Un precio simple.",
  prTitleB: "Sin sorpresas.",
  prSub: "Prueba 7 días gratis. Después, elige cómo continuar.",
  prAnnual: "Anual",
  prAnnualDesc: "12 meses de acceso",
  prAnnualUnit: "/año",
  prA1: "Reproductor completo en todas las TVs compatibles",
  prA2: "Móvil como mando y sincronización",
  prA3: "Guía de programación, PIN parental y varias listas",
  prA4: "Actualizaciones durante el periodo",
  prAnnualCta: "Contratar plan anual",
  prLife: "Vitalicio",
  prLifeDesc: "Pago único",
  prLifeUnit: "para siempre",
  prL1: "Todo lo del plan Anual",
  prL2: "Acceso vitalicio, sin renovación",
  prL3: "Actualizaciones futuras incluidas",
  prL4: "Soporte por correo incluido",
  prLifeCta: "Obtener acceso vitalicio",
  prBest: "Mejor precio",
  prBen1: "7 días gratis",
  prBen2: "Pago seguro",
  prBen3: "Una licencia por equipo",
  prDisclaimer: "Los planes desbloquean el reproductor. Ningún plan incluye canales, listas ni contenido.",

  rsKicker: "Revendedores",
  rsTitle: "¿Tienes clientes con Smart TV?",
  rsLead: "Crea y gestiona activaciones con créditos en tu propio panel.",
  rsCta: "Ser revendedor",

  fiTitle: "Tu TV está a un código QR de distancia.",
  fiLead: "Instala Moova, añade tu lista y mira con el móvil al mando. 7 días gratis, sin tarjeta.",
  fiCta: "Activar mi TV",
  fiTrademarks: "Roku, Roku TV y Roku Streaming Stick son marcas de Roku, Inc. Android, Android TV, Google TV, Chromecast y Google Play son marcas de Google LLC. Apple TV, tvOS, iPhone y App Store son marcas de Apple Inc. Amazon, Fire TV y Amazon Appstore son marcas de Amazon.com, Inc. Samsung, Tizen, LG y webOS pertenecen a sus respectivos dueños. Moova no está afiliada ni respaldada por ninguna de ellas.",
  fiMicro: "Sin tarjeta en la prueba gratis · No vendemos contenido",
};

const de: Dict = {
  metaTitle: "Moova TV — Der Player für deinen Smart TV, mit dem Handy als Fernbedienung",
  metaDesc:
    "Media-Player für Samsung, LG, Roku, Fire TV, Android TV und Apple TV. Deine Playlists, 7-Tage-Programm, Kindersicherung per PIN und das Handy als Fernbedienung. 7 Tage gratis.",

  heroEyebrow: "Media-Player für Smart TVs",
  heroH1a: "Dein TV. Deine Quellen.",
  heroH1b: "Dein Handy hat das Sagen.",
  heroLead:
    "Moova macht aus den Playlists, die du schon hast, einen schönen Katalog auf dem TV. Koppel dein Handy und wähle dort, was du hier siehst.",
  heroCta: "7 Tage gratis testen",
  heroCta2: "Pläne ansehen",
  heroTrust1: "Keine Karte nötig",
  heroTrust2: "Aktivierung per QR-Code",
  heroTrust3: "Wir verkaufen keine Inhalte",
  heroRunsOn: "Läuft auf",

  stBrowse: "Am Handy auswählen",
  stSelect: "Titel ausgewählt",
  stSend: "Wird an den TV gesendet",
  stPlaying: "Läuft auf dem TV",
  stRemote: "Dein Handy ist jetzt die Fernbedienung",
  stWatch: "Auf dem TV ansehen",
  stHome: "Start",
  stMovies: "Filme",
  stSeries: "Serien",
  stLive: "Live",
  stList: "Meine Liste",
  stContinue: "Weiterschauen",
  tvRowCw: "Angefangene Filme",
  tvRowLast: "Zuletzt Hinzugefügte Filme",
  stSettings: "Einstellungen",

  devKicker: "Geräte",
  devTitleA: "Dein TV",
  devTitleB: "steht wahrscheinlich schon auf der Liste.",
  devLead:
    "Ein Player, dasselbe Layout und dieselben Funktionen auf jeder Plattform. Wähle deine, um zu sehen, woher sie installiert wird.",
  devAvailable: "Verfügbar",
  devBeta: "Beta",
  devSoon: "Demnächst",
  devStore: "Installation über",
  devModels: "Geräte",
  devRemote: "Funktioniert mit der Original-Fernbedienung und mit dem Handy",
  devPhoneTitle: "Und in der Tasche",
  devPhoneLead: "Die Moova-App fürs Handy koppelt sich mit den kompatiblen TVs.",
  devPhoneAndroid: "Android · Google Play",
  devPhoneIos: "iPhone · App Store",

  dSamsungOs: "Tizen",
  dSamsungNote: "Samsung Smart TVs",
  dSamsungStore: "Samsung TV Apps",
  dLgOs: "webOS",
  dLgNote: "LG Smart TVs",
  dLgStore: "LG Content Store",
  dRokuOs: "Roku OS",
  dRokuNote: "Roku TV Modelle, Roku Express, Roku Streaming Stick und Roku Ultra",
  dRokuStore: "Roku Channel Store",
  dFireOs: "Fire OS und Vega OS",
  dFireNote: "Fire TV Stick, Fire TV Cube und Fire TV Edition TVs",
  dFireStore: "Amazon Appstore",
  dAndroidOs: "Android TV und Google TV",
  dAndroidNote: "TCL, Sony, Philips, Xiaomi, Chromecast mit Google TV und TV-Boxen",
  dAndroidStore: "Google Play",
  dAppleOs: "tvOS",
  dAppleNote: "Apple TV HD und Apple TV 4K",
  dAppleStore: "App Store",

  cmKicker: "Handy als Fernbedienung",
  cmTitleA: "Am Handy wählen.",
  cmTitleB: "Auf dem TV schauen.",
  cm1t: "In Sekunden gekoppelt",
  cm1d: "Öffne Mit TV verbinden in Moova, scanne den QR-Code mit der App oder tippe den Code ein, der auf dem TV steht. Der TV bestätigt, fertig.",
  cm2t: "Stöbern ohne Tippen am TV",
  cm2d:
    "Filme, Serien und Sender auf deinem Handy. Tippe auf einen Titel und er läuft auf dem TV, mit Pause, Lautstärke und Senderwechsel in der Hand.",
  cm3t: "Suchen wie ein Mensch",
  cm3d: "Tippe auf der Handytastatur und sieh die Ergebnisse sofort. Schluss mit Buchstabe für Buchstabe auf der Fernbedienung.",
  cm4t: "Alles synchron",
  cm4d:
    "Favoriten, Weiterschauen und Playlists sind auf TV und Handy gleich. Playlist in der App gewechselt, der TV zieht mit.",
  cmConnect: "Mit TV verbinden",
  cmConnectSub: "Scanne den QR-Code auf deinem TV",
  cmCode: "Oder Code eingeben",
  cmConnected: "TV verbunden",
  cmSearch: "Filme suchen…",
  cmSyncTitle: "Mit dem TV synchron",
  cmSyncFav: "Favoriten",
  cmSyncCw: "Weiterschauen",
  cmSyncPl: "Playlists",
  cmNowPlaying: "Läuft auf dem TV",

  ftKicker: "Funktionen",
  ftTitleA: "Gemacht zum Schauen,",
  ftTitleB: "nicht zum Einrichten.",
  f1t: "Weiter, wo du aufgehört hast",
  f1d:
    "Angefangene Filme und Serien direkt auf dem Startbildschirm. Die Serie merkt sich die Folge, der Film die Minute, und der richtige Button erscheint: Weiter oder Nächste Folge.",
  f2t: "Live-TV mit EPG",
  f2d: "Heute und die nächsten 7 Tage im Live-Modus, mit Catch-up, wann immer dein Server es anbietet.",
  f3t: "Eine Kindersicherung, die hält",
  f3d:
    "Sperre Kategorien per PIN. Sie verschwinden aus Suche und Highlights. Der Privatmodus pausiert den Verlauf, wann immer du willst.",
  f4t: "Mehrere Playlists",
  f4d: "Füge Playlists hinzu, benenne sie um, wechsle und schütze jede per PIN. Am TV oder am Handy.",
  f5t: "Schnelle Suche",
  f5d: "Bildschirmtastatur am TV oder Tippen am Handy, mit Ergebnissen, die die Kindersicherung respektieren.",
  f6t: "Startet schnell",
  f6d:
    "Der Katalog liegt im Cache auf dem Gerät. Moova öffnet direkt auf dem Startbildschirm und lädt erst wieder alles, wenn du es willst.",
  f7t: "7 Sprachen",
  f7d: "Portugiesisch, Englisch, Spanisch, Französisch, Deutsch, Italienisch und Ukrainisch, mit einstellbarem Zeitformat und Zeitzone.",
  f8t: "Wiedergabe, die durchhält",
  f8d: "Sender in TS oder HLS mit automatischem Fallback und Senderwechsel, ohne den Player zu stoppen.",
  ilMovies: "Angefangene Filme",
  ilSeries: "Angefangene Serien",
  ilRecent: "Zuletzt gesehene Sender",
  ilLeft: "Min übrig",
  ilNext: "Nächste Folge",
  ilToday: "Heute",
  ilNow: "Jetzt",
  ilLocked: "Gesperrt",
  ilPin: "PIN eingeben",
  ilPl1: "Wohnzimmer",
  ilPl2: "Schlafzimmer",
  ilPl3: "Ferienhaus",
  ilCached: "Katalog im Cache",
  ilReady: "Bereit",
  ilFallback: "automatischer Fallback",
  ilPrivacy: "Privatmodus",

  stepKicker: "Erste Schritte",
  stepTitle: "Drei Schritte und der TV ist bereit",
  step1t: "Moova auf dem TV installieren und öffnen",
  step1d: "Der erste Bildschirm zeigt einen QR-Code und die Geräteadresse deines TVs. Die 7-Tage-Testphase startet genau dort, ohne Karte.",
  step2t: "Playlist hinzufügen",
  step2d: "Füge die URL deiner Playlist oder deines Medienservers auf der Website oder in der App ein. Der TV lädt den Katalog und du schaust los.",
  step3t: "Schauen und bei Gefallen einen Plan wählen",
  step3d: "Scanne den QR-Code oder öffne moova.tv/activation, wann immer du nach dem Test weitermachen willst.",
  stepNote: "Nach dem Test wählst du den Jahres- oder den Lifetime-Plan. Kein Plan enthält Inhalte.",

  pnpKicker: "Player, kein Anbieter",
  pnpTitleA: "Wir verkaufen den Player.",
  pnpTitleB: "Nie die Inhalte.",
  pnpLead:
    "Moova kommt ohne Sender, ohne Playlists und ohne Inhalte-Abos. Du bringst deine eigenen Quellen mit; wir lassen sie auf deinem TV premium aussehen.",
  pnpYesT: "Was du bekommst",
  pnpYes1: "Einen Premium-Player für deine eigenen Playlists und Medienquellen",
  pnpYes2: "Programmführer, Favoriten, Kindersicherung per PIN und Handy-Sync",
  pnpYes3: "Handy als Fernbedienung auf allen kompatiblen TVs",
  pnpNoT: "Was wir nie verkaufen",
  pnpNo1: "Keine TV-Sender oder Live-Streams",
  pnpNo2: "Keine Playlists, Pakete oder Inhalte-Abos",
  pnpNo3: "Kein Hosting von Inhalten: deine Quellen bleiben deine",

  prKicker: "Pläne",
  prTitleA: "Ein einfacher Preis.",
  prTitleB: "Keine Überraschungen.",
  prSub: "7 Tage gratis testen. Danach entscheidest du, wie es weitergeht.",
  prAnnual: "Jährlich",
  prAnnualDesc: "12 Monate Zugang",
  prAnnualUnit: "/Jahr",
  prA1: "Voller Player auf allen kompatiblen TVs",
  prA2: "Handy als Fernbedienung und Sync",
  prA3: "Programmführer, Kindersicherung per PIN und mehrere Playlists",
  prA4: "Updates während der Laufzeit",
  prAnnualCta: "Jahresplan starten",
  prLife: "Lifetime",
  prLifeDesc: "Einmalige Zahlung",
  prLifeUnit: "für immer",
  prL1: "Alles aus Jährlich",
  prL2: "Lebenslanger Zugang, keine Verlängerung",
  prL3: "Zukünftige Updates inklusive",
  prL4: "E-Mail-Support inklusive",
  prLifeCta: "Lifetime-Zugang holen",
  prBest: "Bestes Angebot",
  prBen1: "7 Tage gratis testen",
  prBen2: "Sichere Zahlung",
  prBen3: "Eine Lizenz pro Gerät",
  prDisclaimer: "Pläne schalten den Player frei. Kein Plan enthält Sender, Playlists oder Inhalte.",

  rsKicker: "Reseller",
  rsTitle: "Hast du Kunden mit Smart TVs?",
  rsLead: "Erstelle und verwalte Aktivierungen mit Guthaben in deinem eigenen Panel.",
  rsCta: "Reseller werden",

  fiTitle: "Dein TV ist nur einen QR-Code entfernt.",
  fiLead: "Installiere Moova, füge deine Playlist hinzu und schau mit dem Handy als Fernbedienung. 7 Tage gratis, ohne Karte.",
  fiCta: "Meinen TV aktivieren",
  fiTrademarks: "Roku, Roku TV und Roku Streaming Stick sind Marken von Roku, Inc. Android, Android TV, Google TV, Chromecast und Google Play sind Marken von Google LLC. Apple TV, tvOS, iPhone und App Store sind Marken von Apple Inc. Amazon, Fire TV und Amazon Appstore sind Marken von Amazon.com, Inc. Samsung, Tizen, LG und webOS gehören ihren jeweiligen Inhabern. Moova ist mit keinem von ihnen verbunden und wird von keinem unterstützt.",
  fiMicro: "Keine Karte im Gratis-Test · Wir verkaufen keine Inhalte",
};

const fr: Dict = {
  metaTitle: "Moova TV — Le lecteur de votre Smart TV, avec le téléphone aux commandes",
  metaDesc:
    "Lecteur multimédia pour Samsung, LG, Roku, Fire TV, Android TV et Apple TV. Vos playlists, un guide sur 7 jours, un code PIN parental et le téléphone comme télécommande. 7 jours gratuits.",

  heroEyebrow: "Lecteur multimédia pour Smart TV",
  heroH1a: "Votre TV. Vos sources.",
  heroH1b: "Votre téléphone aux commandes.",
  heroLead:
    "Moova transforme les playlists que vous avez déjà en un beau catalogue sur la TV. Associez votre téléphone et choisissez là-bas ce que vous regardez ici.",
  heroCta: "Essayer 7 jours gratuits",
  heroCta2: "Voir les offres",
  heroTrust1: "Sans carte bancaire",
  heroTrust2: "Activation par QR code",
  heroTrust3: "Nous ne vendons aucun contenu",
  heroRunsOn: "Fonctionne sur",

  stBrowse: "Choisissez sur le téléphone",
  stSelect: "Titre sélectionné",
  stSend: "Envoi vers la TV",
  stPlaying: "Lecture sur la TV",
  stRemote: "Votre téléphone est la télécommande",
  stWatch: "Regarder sur la TV",
  stHome: "Accueil",
  stMovies: "Films",
  stSeries: "Séries",
  stLive: "Direct",
  stList: "Ma liste",
  stContinue: "Reprendre",
  tvRowCw: "Films en cours",
  tvRowLast: "Films Récemment Ajoutés",
  stSettings: "Paramètres",

  devKicker: "Appareils",
  devTitleA: "Votre TV",
  devTitleB: "est sans doute déjà dans la liste.",
  devLead:
    "Un seul lecteur, la même mise en page et les mêmes fonctions sur chaque plateforme. Choisissez la vôtre pour voir d'où elle s'installe.",
  devAvailable: "Disponible",
  devBeta: "Bêta",
  devSoon: "Bientôt",
  devStore: "S'installe depuis",
  devModels: "Appareils",
  devRemote: "Fonctionne avec la télécommande d'origine et avec le téléphone",
  devPhoneTitle: "Et dans la poche",
  devPhoneLead: "L'app Moova pour téléphone s'associe aux TV compatibles.",
  devPhoneAndroid: "Android · Google Play",
  devPhoneIos: "iPhone · App Store",

  dSamsungOs: "Tizen",
  dSamsungNote: "Smart TV Samsung",
  dSamsungStore: "Samsung TV Apps",
  dLgOs: "webOS",
  dLgNote: "Smart TV LG",
  dLgStore: "LG Content Store",
  dRokuOs: "Roku OS",
  dRokuNote: "Modèles Roku TV, Roku Express, Roku Streaming Stick et Roku Ultra",
  dRokuStore: "Roku Channel Store",
  dFireOs: "Fire OS et Vega OS",
  dFireNote: "Fire TV Stick, Fire TV Cube et TV Fire TV Edition",
  dFireStore: "Amazon Appstore",
  dAndroidOs: "Android TV et Google TV",
  dAndroidNote: "TCL, Sony, Philips, Xiaomi, Chromecast avec Google TV et box TV",
  dAndroidStore: "Google Play",
  dAppleOs: "tvOS",
  dAppleNote: "Apple TV HD et Apple TV 4K",
  dAppleStore: "App Store",

  cmKicker: "Le téléphone comme télécommande",
  cmTitleA: "Choisissez sur le téléphone.",
  cmTitleB: "Regardez sur la TV.",
  cm1t: "Associez en quelques secondes",
  cm1d: "Ouvrez Connecter à la TV dans Moova, scannez le QR avec l'app ou saisissez le code affiché sur la TV. La TV confirme et c'est fait.",
  cm2t: "Naviguez sans taper sur la TV",
  cm2d:
    "Films, séries et chaînes sur votre téléphone. Touchez un titre et il se lance sur la TV, avec pause, volume et changement de chaîne dans la main.",
  cm3t: "Cherchez comme une personne",
  cm3d: "Tapez sur le clavier du téléphone et voyez les résultats tout de suite. Fini la lettre par lettre à la télécommande.",
  cm4t: "Tout est synchronisé",
  cm4d:
    "Favoris, reprise de lecture et playlists restent identiques sur la TV et sur le téléphone. Changez de playlist dans l'app, la TV suit.",
  cmConnect: "Connecter à la TV",
  cmConnectSub: "Scannez le QR affiché sur votre TV",
  cmCode: "Ou saisissez le code",
  cmConnected: "TV connectée",
  cmSearch: "Rechercher des films…",
  cmSyncTitle: "Synchronisé avec la TV",
  cmSyncFav: "Favoris",
  cmSyncCw: "Reprendre",
  cmSyncPl: "Playlists",
  cmNowPlaying: "En lecture sur la TV",

  ftKicker: "Fonctions",
  ftTitleA: "Fait pour regarder,",
  ftTitleB: "pas pour configurer.",
  f1t: "Reprenez où vous en étiez",
  f1d:
    "Films et séries en cours directement sur l'écran d'accueil. La série retient l'épisode, le film retient la minute, et le bon bouton apparaît : Reprendre ou Épisode suivant.",
  f2t: "TV en direct avec EPG",
  f2d: "Aujourd'hui et les 7 prochains jours en mode direct, avec le rattrapage dès que votre serveur le propose.",
  f3t: "Un code PIN parental qui tient",
  f3d:
    "Verrouillez des catégories par code PIN. Elles disparaissent de la recherche et des mises en avant. Le mode privé met l'historique en pause quand vous voulez.",
  f4t: "Plusieurs playlists",
  f4d: "Ajoutez, renommez, changez et protégez chaque playlist par code PIN. Depuis la TV ou le téléphone.",
  f5t: "Recherche rapide",
  f5d: "Clavier à l'écran sur la TV ou saisie depuis le téléphone, avec des résultats filtrés par le code PIN parental.",
  f6t: "S'ouvre vite",
  f6d:
    "Le catalogue est mis en cache sur l'appareil. Moova s'ouvre directement sur l'accueil et ne retélécharge tout que si vous le demandez.",
  f7t: "7 langues",
  f7d: "Portugais, anglais, espagnol, français, allemand, italien et ukrainien, avec format d'heure et fuseau réglables.",
  f8t: "Une lecture qui tient",
  f8d: "Chaînes en TS ou HLS avec bascule automatique et changement de chaîne sans arrêter le lecteur.",
  ilMovies: "Films en cours",
  ilSeries: "Séries en cours",
  ilRecent: "Chaînes récentes",
  ilLeft: "min restantes",
  ilNext: "Épisode suivant",
  ilToday: "Aujourd'hui",
  ilNow: "Maintenant",
  ilLocked: "Verrouillée",
  ilPin: "Saisir le PIN",
  ilPl1: "Salon",
  ilPl2: "Chambre",
  ilPl3: "Maison de campagne",
  ilCached: "Catalogue en cache",
  ilReady: "Prêt",
  ilFallback: "bascule automatique",
  ilPrivacy: "Mode privé",

  stepKicker: "Pour commencer",
  stepTitle: "Trois étapes et la TV est prête",
  step1t: "Installez et ouvrez Moova sur la TV",
  step1d: "Le premier écran affiche un QR code et l'adresse de votre appareil. L'essai de 7 jours commence là, sans carte.",
  step2t: "Ajoutez votre playlist",
  step2d: "Collez l'URL de votre playlist ou de votre serveur multimédia sur le site ou dans l'app. La TV charge le catalogue et vous regardez.",
  step3t: "Regardez, puis choisissez une offre si ça vous plaît",
  step3d: "Scannez le QR ou allez sur moova.tv/activation quand vous voulez continuer après l'essai.",
  stepNote: "Après l'essai, choisissez l'offre annuelle ou à vie. Aucune offre n'inclut de contenu.",

  pnpKicker: "Lecteur, pas fournisseur",
  pnpTitleA: "Nous vendons le lecteur.",
  pnpTitleB: "Jamais le contenu.",
  pnpLead:
    "Moova est livré sans chaînes, sans playlists et sans abonnement à des contenus. Vous apportez vos propres sources ; nous les rendons premium sur votre TV.",
  pnpYesT: "Ce que vous obtenez",
  pnpYes1: "Un lecteur premium pour vos propres playlists et sources multimédias",
  pnpYes2: "Guide des programmes, favoris, code PIN parental et synchronisation avec le téléphone",
  pnpYes3: "Le téléphone comme télécommande sur toutes les TV compatibles",
  pnpNoT: "Ce que nous ne vendons jamais",
  pnpNo1: "Aucune chaîne de TV ni flux en direct",
  pnpNo2: "Aucune playlist, aucun bouquet ni abonnement à des contenus",
  pnpNo3: "Aucun hébergement de contenu : vos sources restent les vôtres",

  prKicker: "Offres",
  prTitleA: "Un prix simple.",
  prTitleB: "Sans surprise.",
  prSub: "Essayez 7 jours gratuits. Ensuite, choisissez comment continuer.",
  prAnnual: "Annuel",
  prAnnualDesc: "12 mois d'accès",
  prAnnualUnit: "/an",
  prA1: "Lecteur complet sur toutes les TV compatibles",
  prA2: "Téléphone comme télécommande et synchronisation",
  prA3: "Guide des programmes, code PIN parental et plusieurs playlists",
  prA4: "Mises à jour pendant la période",
  prAnnualCta: "Choisir l'offre annuelle",
  prLife: "À vie",
  prLifeDesc: "Paiement unique",
  prLifeUnit: "pour toujours",
  prL1: "Tout l'offre Annuelle",
  prL2: "Accès à vie, sans renouvellement",
  prL3: "Mises à jour futures incluses",
  prL4: "Support par e-mail inclus",
  prLifeCta: "Obtenir l'accès à vie",
  prBest: "Meilleur rapport",
  prBen1: "7 jours gratuits",
  prBen2: "Paiement sécurisé",
  prBen3: "Une licence par appareil",
  prDisclaimer: "Les offres débloquent le lecteur. Aucune offre n'inclut de chaînes, de playlists ni de contenu.",

  rsKicker: "Revendeurs",
  rsTitle: "Vous avez des clients avec une Smart TV ?",
  rsLead: "Créez et gérez des activations avec des crédits dans votre propre tableau de bord.",
  rsCta: "Devenir revendeur",

  fiTitle: "Votre TV est à un QR code de distance.",
  fiLead: "Installez Moova, ajoutez votre playlist et regardez avec le téléphone aux commandes. 7 jours gratuits, sans carte.",
  fiCta: "Activer ma TV",
  fiTrademarks: "Roku, Roku TV et Roku Streaming Stick sont des marques de Roku, Inc. Android, Android TV, Google TV, Chromecast et Google Play sont des marques de Google LLC. Apple TV, tvOS, iPhone et App Store sont des marques d'Apple Inc. Amazon, Fire TV et Amazon Appstore sont des marques d'Amazon.com, Inc. Samsung, Tizen, LG et webOS appartiennent à leurs propriétaires respectifs. Moova n'est affiliée à aucun d'eux et n'est soutenue par aucun d'eux.",
  fiMicro: "Sans carte pour l'essai gratuit · Nous ne vendons aucun contenu",
};

const uk: Dict = {
  metaTitle: "Moova TV — Плеєр для вашого Smart TV, яким керує ваш телефон",
  metaDesc:
    "Медіаплеєр для Samsung, LG, Roku, Fire TV, Android TV та Apple TV. Ваші плейлисти, телегід на 7 днів, батьківський PIN-код і телефон як пульт. 7 днів безкоштовно.",

  heroEyebrow: "Медіаплеєр для Smart TV",
  heroH1a: "Ваш телевізор. Ваші джерела.",
  heroH1b: "Ваш телефон керує.",
  heroLead:
    "Moova перетворює плейлисти, які ви вже маєте, на красивий каталог на телевізорі. Під’єднайте телефон і обирайте там, що дивитися тут.",
  heroCta: "Спробуйте безкоштовно 7 днів",
  heroCta2: "Переглянути плани",
  heroTrust1: "Без картки",
  heroTrust2: "Активація за QR-кодом",
  heroTrust3: "Ми ніколи не продаємо контент",
  heroRunsOn: "Працює на",

  stBrowse: "Обирайте на телефоні",
  stSelect: "Фільм вибрано",
  stSend: "Надсилаємо на телевізор",
  stPlaying: "Відтворюється на телевізорі",
  stRemote: "Тепер ваш телефон — пульт",
  stWatch: "Дивитися на телевізорі",
  stHome: "Головна",
  stMovies: "Фільми",
  stSeries: "Серіали",
  stLive: "Прямий ефір",
  stList: "Мій список",
  stContinue: "Продовжити перегляд",
  tvRowCw: "Фільми, які ви дивитеся",
  tvRowLast: "Останні додані фільми",
  stSettings: "Налаштування",

  devKicker: "Пристрої",
  devTitleA: "Ваш телевізор",
  devTitleB: "напевно вже в списку.",
  devLead:
    "Один плеєр, однаковий вигляд і ті самі функції на кожній платформі. Оберіть свою, щоб побачити, звідки встановлювати.",
  devAvailable: "Доступно",
  devBeta: "Бета",
  devSoon: "Незабаром",
  devStore: "Встановлюється з",
  devModels: "Пристрої",
  devRemote: "Працює з оригінальним пультом і з вашим телефоном",
  devPhoneTitle: "І у вашій кишені",
  devPhoneLead: "Застосунок Moova для телефона під’єднується до сумісних телевізорів.",
  devPhoneAndroid: "Android · Google Play",
  devPhoneIos: "iPhone · App Store",

  dSamsungOs: "Tizen",
  dSamsungNote: "Телевізори Samsung Smart TV",
  dSamsungStore: "Samsung TV Apps",
  dLgOs: "webOS",
  dLgNote: "Телевізори LG Smart TV",
  dLgStore: "LG Content Store",
  dRokuOs: "Roku OS",
  dRokuNote: "Телевізори Roku TV, Roku Express, Roku Streaming Stick і Roku Ultra",
  dRokuStore: "Roku Channel Store",
  dFireOs: "Fire OS і Vega OS",
  dFireNote: "Fire TV Stick, Fire TV Cube і телевізори Fire TV Edition",
  dFireStore: "Amazon Appstore",
  dAndroidOs: "Android TV і Google TV",
  dAndroidNote: "TCL, Sony, Philips, Xiaomi, Chromecast із Google TV і ТВ-приставки",
  dAndroidStore: "Google Play",
  dAppleOs: "tvOS",
  dAppleNote: "Apple TV HD і Apple TV 4K",
  dAppleStore: "App Store",

  cmKicker: "Телефон як пульт",
  cmTitleA: "Обирайте на телефоні.",
  cmTitleB: "Дивіться на телевізорі.",
  cm1t: "Під’єднання за секунди",
  cm1d: "Відкрийте «Під’єднати до телевізора» в Moova, відскануйте QR-код застосунком або введіть код, показаний на телевізорі. Телевізор підтвердить — і готово.",
  cm2t: "Переглядайте без набору на телевізорі",
  cm2d:
    "Фільми, серіали й канали на вашому телефоні. Торкніться назви — і вона відтворюється на телевізорі, а пауза, гучність і перемикання каналів у вас у руці.",
  cm3t: "Шукайте як людина",
  cm3d: "Набирайте на клавіатурі телефона й одразу бачте результати. Більше жодного набору літера за літерою пультом.",
  cm4t: "Усе синхронізовано",
  cm4d:
    "Обране, продовження перегляду та плейлисти однакові на телевізорі й на телефоні. Змініть плейлист у застосунку — і телевізор зробить те саме.",
  cmConnect: "Під’єднати до телевізора",
  cmConnectSub: "Відскануйте QR-код, показаний на телевізорі",
  cmCode: "Або введіть код",
  cmConnected: "Телевізор під’єднано",
  cmSearch: "Пошук фільмів…",
  cmSyncTitle: "Синхронізовано з телевізором",
  cmSyncFav: "Обране",
  cmSyncCw: "Продовжити перегляд",
  cmSyncPl: "Плейлисти",
  cmNowPlaying: "Відтворюється на телевізорі",

  ftKicker: "Можливості",
  ftTitleA: "Створено для перегляду,",
  ftTitleB: "а не для налаштування.",
  f1t: "Продовжуйте з того місця, де зупинилися",
  f1d:
    "Незавершені фільми та серіали — одразу на головному екрані. Серіал пам’ятає епізод, фільм пам’ятає хвилину, і з’являється потрібна кнопка: «Продовжити» або «Наступний епізод».",
  f2t: "Телебачення наживо з EPG",
  f2d: "Сьогодні й наступні 7 днів у режимі прямого ефіру, з переглядом пропущеного, якщо ваш сервер його підтримує.",
  f3t: "Батьківський PIN-код, який справді працює",
  f3d:
    "Блокуйте категорії PIN-кодом. Вони зникають із пошуку та добірок. Режим приватності зупиняє історію перегляду, коли вам потрібно.",
  f4t: "Кілька плейлистів",
  f4d: "Додавайте, перейменовуйте, перемикайте й захищайте кожен плейлист PIN-кодом. З телевізора або з телефона.",
  f5t: "Швидкий пошук",
  f5d: "Екранна клавіатура на телевізорі або набір із телефона, з результатами, відфільтрованими за батьківським PIN-кодом.",
  f6t: "Швидко запускається",
  f6d:
    "Каталог кешується на пристрої. Moova відкривається одразу на головному екрані й завантажує все знову лише на ваш запит.",
  f7t: "7 мов",
  f7d: "Португальська, англійська, іспанська, французька, німецька, італійська та українська, з налаштуванням формату часу й часового поясу.",
  f8t: "Відтворення, яке не підводить",
  f8d: "Канали в TS або HLS з автоматичним резервним перемиканням і зміна каналів без зупинки плеєра.",
  ilMovies: "Незавершені фільми",
  ilSeries: "Незавершені серіали",
  ilRecent: "Недавні канали",
  ilLeft: "хв залишилося",
  ilNext: "Наступний епізод",
  ilToday: "Сьогодні",
  ilNow: "Зараз",
  ilLocked: "Заблоковано",
  ilPin: "Введіть PIN-код",
  ilPl1: "Вітальня",
  ilPl2: "Спальня",
  ilPl3: "Дача",
  ilCached: "Каталог у кеші",
  ilReady: "Готово",
  ilFallback: "автоматичне резервування",
  ilPrivacy: "Режим приватності",

  stepKicker: "Початок роботи",
  stepTitle: "Три кроки — і телевізор готовий",
  step1t: "Встановіть і відкрийте Moova на телевізорі",
  step1d: "На першому екрані показано QR-код і адресу пристрою вашого телевізора. Пробний період на 7 днів починається одразу, без картки.",
  step2t: "Додайте свій плейлист",
  step2d: "Вставте URL плейлиста або медіасервера на сайті чи в застосунку. Телевізор завантажить каталог — і ви вже дивитеся.",
  step3t: "Дивіться, а потім оберіть план, якщо сподобалося",
  step3d: "Відскануйте QR-код або перейдіть на moova.tv/activation, коли захочете продовжити після пробного періоду.",
  stepNote: "Після пробного періоду оберіть річний або довічний план. Жоден план не включає контент.",

  pnpKicker: "Плеєр, а не провайдер",
  pnpTitleA: "Ми продаємо плеєр.",
  pnpTitleB: "Ніколи не контент.",
  pnpLead:
    "Moova постачається без каналів, плейлистів і підписок на контент. Ви додаєте власні джерела, а ми робимо так, щоб вони виглядали преміально на вашому телевізорі.",
  pnpYesT: "Що ви отримуєте",
  pnpYes1: "Преміальний плеєр для ваших власних плейлистів і медіаджерел",
  pnpYes2: "Телегід, обране, батьківський PIN-код і синхронізація з телефоном",
  pnpYes3: "Телефон як пульт на кожному сумісному телевізорі",
  pnpNoT: "Чого ми ніколи не продаємо",
  pnpNo1: "Жодних телеканалів чи прямих трансляцій",
  pnpNo2: "Жодних плейлистів, пакетів чи підписок на контент",
  pnpNo3: "Жодного хостингу контенту: ваші джерела залишаються вашими",

  prKicker: "Плани",
  prTitleA: "Одна проста ціна.",
  prTitleB: "Без сюрпризів.",
  prSub: "Спробуйте безкоштовно 7 днів. Потім оберіть, як продовжити.",
  prAnnual: "Річний",
  prAnnualDesc: "12 місяців доступу",
  prAnnualUnit: "/рік",
  prA1: "Повний плеєр на кожному сумісному телевізорі",
  prA2: "Телефон як пульт і синхронізація",
  prA3: "Телегід, батьківський PIN-код і кілька плейлистів",
  prA4: "Оновлення протягом періоду",
  prAnnualCta: "Обрати річний план",
  prLife: "Назавжди",
  prLifeDesc: "Одноразовий платіж",
  prLifeUnit: "назавжди",
  prL1: "Усе з річного плану",
  prL2: "Довічний доступ, без поновлення",
  prL3: "Майбутні оновлення включено",
  prL4: "Підтримка електронною поштою включена",
  prLifeCta: "Отримати довічний доступ",
  prBest: "Найвигідніше",
  prBen1: "7 днів безкоштовно",
  prBen2: "Безпечна оплата",
  prBen3: "Одна ліцензія на пристрій",
  prDisclaimer: "Плани розблоковують плеєр. Жоден план не включає канали, плейлисти чи контент.",

  rsKicker: "Реселери",
  rsTitle: "Маєте клієнтів зі Smart TV?",
  rsLead: "Створюйте активації та керуйте ними за кредити у власній панелі.",
  rsCta: "Стати реселером",

  fiTitle: "Ваш телевізор — на відстані одного QR-коду.",
  fiLead: "Встановіть Moova, додайте плейлист і дивіться, керуючи з телефона. 7 днів безкоштовно, без картки.",
  fiCta: "Активувати мій телевізор",
  fiTrademarks: "Roku, Roku TV і Roku Streaming Stick є торговими марками Roku, Inc. Android, Android TV, Google TV, Chromecast і Google Play є торговими марками Google LLC. Apple TV, tvOS, iPhone і App Store є торговими марками Apple Inc. Amazon, Fire TV і Amazon Appstore є торговими марками Amazon.com, Inc. Samsung, Tizen, LG і webOS належать їхнім відповідним власникам. Moova не пов’язана з жодною з цих компаній і не схвалена ними.",
  fiMicro: "Без картки на пробний період · Ми ніколи не продаємо контент",
};

const dicts: Record<Lang, Dict> = { en, pt, es, de, fr, uk };

export function tHome(key: string, lang: Lang): string {
  return dicts[lang]?.[key] ?? en[key] ?? `[home.${key}]`;
}
